import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 600 : false,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const consent = Cookies.get("cookie-consent");
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    Cookies.set("cookie-consent", "accepted", { expires: 365 });
    setVisible(false);
  };

  const declineCookies = () => {
    setVisible(false);
  };

  const modal = (
    <AnimatePresence>
      {visible && (
        /* Positioning wrapper — CSS transform para centralizar no desktop,
           sem interferir nas animações do Framer Motion */
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            ...(isMobile
              ? { bottom: 0, left: 0, right: 0 }
              : {
                  bottom: "1.75rem",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "min(500px, calc(100vw - 2rem))",
                }),
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 80 : 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: isMobile ? 80 : 20,
              scale: isMobile ? 1 : 0.97,
            }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{
              background: "#ffffff",
              borderRadius: isMobile ? "20px 20px 0 0" : "20px",
              boxShadow: isMobile
                ? "0 -8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(147,240,144,0.2)"
                : "0 32px 72px rgba(0,0,0,0.14), 0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(147,240,144,0.25)",
              overflow: "hidden",
              fontFamily: "Poppins, sans-serif",
            }}
            role="dialog"
            aria-modal="true"
            aria-label={t("cookieConsent.header")}
          >
            {/* Gradient accent bar */}
            <div
              style={{
                height: "3.5px",
                background: "linear-gradient(90deg, #93F090 0%, #60BFB7 100%)",
              }}
            />

            {/* Drag handle — mobile only */}
            {isMobile && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: "0.625rem",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "4px",
                    borderRadius: "2px",
                    background: "#dee2e6",
                  }}
                />
              </div>
            )}

            <div
              style={{
                padding: isMobile
                  ? "1rem 1.25rem calc(1.5rem + env(safe-area-inset-bottom, 0px))"
                  : "1.5rem 1.75rem 1.625rem",
              }}
            >
              {/* Header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: "0.75rem",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: isMobile ? "36px" : "42px",
                      height: isMobile ? "36px" : "42px",
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, rgba(147,240,144,0.18) 0%, rgba(96,191,183,0.18) 100%)",
                      border: "1px solid rgba(147,240,144,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isMobile ? "1.1rem" : "1.25rem",
                      flexShrink: 0,
                      userSelect: "none",
                    }}
                  >
                    🍪
                  </div>

                  {/* Title */}
                  <h3
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "0.9375rem" : "1rem",
                      fontWeight: 600,
                      color: "#1a1a2e",
                      lineHeight: 1.35,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {t("cookieConsent.header")}
                  </h3>
                </div>

                {/* Close button */}
                <button
                  onClick={declineCookies}
                  aria-label="Fechar"
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#adb5bd",
                    flexShrink: 0,
                    transition: "background 0.15s ease, color 0.15s ease",
                    padding: 0,
                    marginTop: "1px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f1f3f5";
                    e.currentTarget.style.color = "#495057";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#adb5bd";
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M1 1L11 11M11 1L1 11"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Divider */}
              <div
                style={{
                  height: "1px",
                  background:
                    "linear-gradient(90deg, #e9ecef 0%, transparent 100%)",
                  margin: "0 0 0.875rem",
                }}
              />

              {/* Body */}
              <p
                style={{
                  margin: "0 0 1.25rem",
                  fontSize: isMobile ? "0.8125rem" : "0.8375rem",
                  color: "#6c757d",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                {t("cookieConsent.body")}
              </p>

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "0.625rem",
                  alignItems: "stretch",
                  justifyContent: isMobile ? "stretch" : "flex-end",
                }}
              >
                {/* Accept — primeiro no mobile (destaque maior) */}
                <button
                  onClick={acceptCookies}
                  style={{
                    order: isMobile ? -1 : 1,
                    padding: isMobile ? "0.75rem 1.375rem" : "0.5rem 1.375rem",
                    borderRadius: "10px",
                    border: "none",
                    background:
                      "linear-gradient(135deg, #93F090 0%, #60BFB7 100%)",
                    color: "#1a3a1a",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    fontFamily: "Poppins, sans-serif",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(147,240,144,0.38)",
                    transition: "all 0.18s ease",
                    letterSpacing: "0.01em",
                    lineHeight: 1,
                    width: isMobile ? "100%" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(147,240,144,0.52)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 14px rgba(147,240,144,0.38)";
                  }}
                >
                  {t("cookieConsent.acceptButton")}
                </button>

                {/* Decline */}
                <button
                  onClick={declineCookies}
                  style={{
                    order: isMobile ? 1 : 0,
                    padding: isMobile ? "0.75rem 1.125rem" : "0.5rem 1.125rem",
                    borderRadius: "10px",
                    border: "1.5px solid #dee2e6",
                    background: "transparent",
                    color: "#6c757d",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    fontFamily: "Poppins, sans-serif",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    letterSpacing: "0.01em",
                    lineHeight: 1,
                    width: isMobile ? "100%" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#adb5bd";
                    e.currentTarget.style.color = "#343a40";
                    e.currentTarget.style.background = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#dee2e6";
                    e.currentTarget.style.color = "#6c757d";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {t("cookieConsent.declineButton")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};
